import { Breadcrumb, Button, Drawer, Empty, Flex, Form, Input, Popconfirm, Table, Tag, Tooltip, Typography, notification, theme } from 'antd';
import { HomeOutlined, DeleteOutlined, EyeOutlined, EditOutlined, LockOutlined, UnlockOutlined, FilterOutlined, WarningOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react'
import MasterDataAPI from '../../restAPI/MasterDataAPI';
import { useSelector } from 'react-redux';
import FilterComponent from '../../utils/FilterComponent';
import CommonSkeletonLoader from '../../helperComponents/CommonSkeleton';
import useCurrentCompany from '../../hooks/useCurrentCompany';
const { Search } = Input;
const { Text, Link } = Typography;

export default function Branch() {
    const { companyId, token, userCompany, role } = useSelector(state => state.auth);
    const [form] = Form.useForm();
    const formValues = Form.useWatch([], form);
    const [branchId, setBranchId] = useState("");
    const [btnPermission, setBtnPermission] = useState({
        "add": false,
        "edit": false,
        "delete": false,
        "view": false,
        "activeInactive": false
    });

    const [actionState, setActionState] = useState('add');
    const [searchInputValue, setSearchInputValue] = useState("");
    const [initialBranchData, setInitialBranchData] = useState({ code: "", name: "" });    
    const currentCompanyInfo = useCurrentCompany();
    const [branchData, setBranchData] = useState([]);
    const [filterValues, setFilterValues] = useState([]);
    const [tempBranchData, setTempBranchData] = useState([]);
    const [open, setOpen] = useState(false);
    const [openFilter, setOpenFilter] = useState(false);
    const [showPopconfirm, setShowPopconfirm] = useState(false);
    const [cancelButtonDisabled, setCancelButtonDisabled] = useState(false);
    const [isBranchListLoading, setIsBranchListLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [api, contextHolder] = notification.useNotification();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const openNotificationWithIcon = (type, message, description, placement) => {
        api[type]({
            message: message,
            description: description,
            placement
        });
    };

    const columns = [
        {
            title: 'Branch Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Branch Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                record.status ? (<Tag color='purple' style={{ width: 90, textAlign: 'center' }}>Active</Tag>) : (<Tag color='warning' style={{ width: 90, textAlign: 'center' }}>Inactive</Tag>)
            ),
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Flex gap="small" justify="center" align="center" vertical>
                    <Flex gap="middle" wrap="wrap">
                        {btnPermission.edit &&
                            <Tooltip title="Edit Branch" placement="top">
                                <Button type="primary" disabled={!record.status} icon={<EditOutlined />} size='small' onClick={() => editHandler(record)} />
                            </Tooltip>
                        }
                        {/* <Button icon={<EyeOutlined />} disabled={true} size='small' /> */}
                        {
                            btnPermission.activeInactive && (
                                <Tooltip title="Active/Inactive Branch" placement="top">
                                    <Popconfirm
                                        title={record.status ? "Do you want to inactivate the branch?" : "Do you want to reactivate the branch?"}
                                        onConfirm={() => editStatusHandler(record)}
                                        onCancel={() => openNotificationWithIcon('error', 'Abort', `Status update cancelled`, 'bottomLeft')}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <Button icon={record.status ? <LockOutlined /> : <UnlockOutlined />} size='small' />
                                    </Popconfirm>
                                </Tooltip>
                            )
                        }
                        {btnPermission.delete && (
                            <Tooltip title="Delete Branch" placement="top">
                                <Popconfirm
                                    title="Do you want to delete this branch?"
                                    onConfirm={() => deleteHandler(record)}
                                    onCancel={() => openNotificationWithIcon('error', 'Abort', `Delete cancelled`, 'bottomLeft')}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button danger type="primary" disabled={record.status} icon={<DeleteOutlined />} size='small' />
                                </Popconfirm>
                            </Tooltip>
                        )
                        }
                    </Flex>
                </Flex>
            ),
        },
    ];

    const showFilterDrawer = () => {
        setOpenFilter(true);
    }
    const onCloseFilter = () => {
        setOpenFilter(false);
    }
    
    const getBranchCodeRules =  useCallback(() => [
        {
            required: true,
            message: 'Branch code is required',
        },
        {
            max: 10,
            message: 'Branch code must be less than 10 characters',
        },
        {
            validator: (_, value) => {
                if (value && value.trim().length === 0) {
                    return Promise.reject(new Error('Branch Code is not valid.'));
                }
                return Promise.resolve();
            },
        },
    ],[]);

    const getBranchNameRules = useCallback(() => [
        {
            required: true,
            message: 'Branch name is required',
        },
        {
            max: 50,
            message: 'Branch name must be less than 50 characters',
        },
        {
            validator: (_, value) => {
                if (value && value.trim().length === 0) {
                    return Promise.reject(new Error('Branch Name is not valid.'));
                }
                return Promise.resolve();
            },
        },
    ],[]);


    const onClose = () => {
        form.resetFields();        
        setInitialBranchData({ code: "", name: "" });
        setBranchId("");
        setOpen(false);
        setShowPopconfirm(false)
        setTimeout(() => {
            setActionState('add')
        }, 200)
    };

    const applySearchAndFilters = (data, searchText, filters) => {
        return data.filter((branch) => {
            const matchesSearch = searchText ? 
                (branch.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
                (branch.code || '').toLowerCase().includes(searchText.toLowerCase()) 
                : true

            const matchesFilters = filters.length > 0 ?
                filters.every(({ key, value }) => {
                    if (!value) return false;
                    const itemValue = (branch[key]?.toString() || '').toLowerCase();
                    return itemValue.startsWith(value.toLowerCase());
                })
                : true

            return matchesSearch && matchesFilters
        })
    }

    const deleteHandler = async (record) => {
        await MasterDataAPI.deleteBranch(record.id, companyId, token)
            .then((res) => {
                openNotificationWithIcon('success', 'Successful', `${res.message}`, 'bottomLeft');

                // 1. Remove from source list
                const updatedSourceList = branchData.filter(branch => branch.id !== record.id);
                setBranchData(updatedSourceList);

                // 2. Reapply current search & filters
                const matchesSearch = (branch) => {
                    return searchInputValue
                        ? (branch.name || '').toLowerCase().includes(searchInputValue.toLowerCase()) ||
                        (branch.code || '').toLowerCase().includes(searchInputValue.toLowerCase())
                        : true;
                };

                const matchesFilter = (branch) => {
                    return filterValues.length > 0
                        ? filterValues.every(({ key, value }) => {
                            if (!value) return false;
                            const itemValue = (branch[key]?.toString() || '').toLowerCase();
                            return itemValue.startsWith(value.toLowerCase());
                        })
                        : true;
                };

                const filtered = updatedSourceList.filter(branch =>
                    matchesSearch(branch) && matchesFilter(branch)
                );

                setTempBranchData(filtered);
            })
            .catch((err) => {
                if (err.response) {
                    openNotificationWithIcon('error', 'Branch deletion failed', `${err.response.data.message}`, 'bottomLeft');
                } else {
                    openNotificationWithIcon('error', 'Branch deletion failed', `${err.message}`, 'bottomLeft');
                }
            });
    };

    const editHandler = (records) => {
        setBranchId(records.id);            
        setInitialBranchData({
            code: records.code,
            name: records.name,
        });
        form.setFieldsValue({
            code: records.code,
            name: records.name,
        });
        setActionState("edit")
        setOpen(true);
    }

    const editStatusHandler = async (record) => {
        const formData = {
            status: !record.status,
        };

        await MasterDataAPI.updateStatusBranch(record.id, formData, companyId, token)
            .then((res) => {
                openNotificationWithIcon('success', 'Successful', `${res.message}`, 'bottomLeft');

                const updatedBranch = res.data?.[0]; // since API returns an array

                if (!updatedBranch) return;

                // Step 1: update the source list
                const updatedSourceList = branchData.map(branch =>
                    branch.id === updatedBranch.id ? updatedBranch : branch
                );
                setBranchData(updatedSourceList);

                // Step 2: reapply search + filters
                const matchesSearch = (branch) => {
                    return searchInputValue
                        ? (branch.name || '').toLowerCase().includes(searchInputValue.toLowerCase()) ||
                        (branch.code || '').toLowerCase().includes(searchInputValue.toLowerCase())
                        : true;
                };

                const matchesFilter = (branch) => {
                    return filterValues.length > 0
                        ? filterValues.every(({ key, value }) => {
                            if (!value) return false;
                            const itemValue = (branch[key]?.toString() || '').toLowerCase();
                            return itemValue.startsWith(value.toLowerCase());
                        })
                        : true;
                };

                const filtered = updatedSourceList.filter(branch =>
                    matchesSearch(branch) && matchesFilter(branch)
                );

                setTempBranchData(filtered);
            })
            .catch((err) => {
                console.log('error in branch edit :', err)
                if (err.response) {
                    openNotificationWithIcon('error', 'Branch status change failed', `${err.response.data.message}`, 'bottomLeft');
                } else {
                    openNotificationWithIcon('error', 'Branch status change failed', `${err.message}`, 'bottomLeft');
                }
            });
    };


    const getBranchData = async () => {
        try {
            const response = await MasterDataAPI.getBranchDataByCompanyIdForMasterData(companyId, token);
            setBranchData(response?.data?.data || [])
            setTempBranchData(response?.data?.data || [])
            setIsBranchListLoading(false)
        } catch (error) {
            setIsBranchListLoading(false)
            setBranchData([])
            setTempBranchData([])
            console.log("get branch data error :", error);
        }
    }

    const handleFilterDetailsChange = (value) => {
        setFilterValues(value);
    }

    // used for advance filter
    const filterDataByCriteria = (data, criteria) => {
        return data.filter(item => {
            return criteria.every(({ key, value }) => {
                // to-do put for status also
                if (!item[key] || !value) return false;
                const itemValue = (item[key]?.toString() || '').toLowerCase();
                const lowerCasePrefix = (value || '').toLowerCase();
                return itemValue.includes(lowerCasePrefix);
            });
        });
    };

    const handleSearchFunction = (value) => {
        setSearchInputValue(value);
        if (value) {
            if (filterValues.length > 0) {
                const filteredData = tempBranchData.filter(item =>
                    (item.name || '').toLowerCase().includes(value.toLowerCase()) ||
                    (item.code || '').toLowerCase().includes(value.toLowerCase())
                );
                setTempBranchData(filteredData);
            }
            else {
                const filteredData = branchData.filter(item =>
                    (item.name || '').toLowerCase().includes(value.toLowerCase()) ||
                    (item.code || '').toLowerCase().includes(value.toLowerCase())
                );
                setTempBranchData(filteredData);
            }
        } else {
            if (filterValues.length > 0) {
                const filterData = filterDataByCriteria(branchData, filterValues);
                setTempBranchData(filterData);
            } else {
                setTempBranchData(branchData);
            }
        }
    };

    const handleFilterSearchFunction = (value) => {
        if (value) {
            if (searchInputValue?.length > 0) {
                const filteredData = branchData.filter(item =>
                    (item.name || '').toLowerCase().includes(searchInputValue.toLowerCase()) ||
                    (item.code || '').toLowerCase().includes(searchInputValue.toLowerCase())
                );
                const filterData2 = filterDataByCriteria(filteredData, value);
                setTempBranchData(filterData2);

            } else {
                const filterData = filterDataByCriteria(branchData, value);
                setTempBranchData(filterData);
            }
        } else {
            if (searchInputValue?.length > 0) {
                const filteredData = branchData.filter(item =>
                    (item.name || '').toLowerCase().includes(searchInputValue.toLowerCase()) ||
                    (item.code || '').toLowerCase().includes(searchInputValue.toLowerCase())
                );
                setTempBranchData(filteredData);
            } else {
                setTempBranchData(branchData);
            }
        }
    };

    const handleRemoveTag = (key) => {
        setFilterValues(prev =>
            prev.filter(item => item.key !== key)
        );
    }
   
    useEffect(() => {
        setIsBranchListLoading(true);
        getBranchData()
    }, [companyId]);

    useEffect(() => {
        handleFilterSearchFunction(filterValues);
        setCurrentPage(1); // Reset to page 1 when filters change
    }, [filterValues])

    useEffect(() => {
        handleSearchFunction(searchInputValue)
        setCurrentPage(1); // Reset to page 1 when search changes
    }, [searchInputValue])

    const validateBeforeSubmit = async () => {
        try {
            await form.validateFields();
            return true;
        } catch (errorInfo) {
            return false;
        }
    };
    const isFormModified = useCallback(() => {
        if (actionState !== 'edit') return true; 
        
        if (!formValues) return false;
        
        const currentValues = form.getFieldsValue();
        const codeChanged = (currentValues.code || '').trim() !== (initialBranchData.code || '').trim();
        const nameChanged = (currentValues.name || '').trim() !== (initialBranchData.name || '').trim();
        
        return codeChanged || nameChanged;
    }, [actionState, formValues, initialBranchData, form]);

    const handleSubmit = async () => {
        const validation = await validateBeforeSubmit();
        if (!validation) {
            return;
        }

        const formValues = form.getFieldsValue();        
        const trimmedPayload = {
            code: formValues.code.trim(),
            name: formValues.name.trim(),
        };

        if (actionState === "add") {
            try{
                const {message,data} = await MasterDataAPI.createBranch(trimmedPayload, companyId, token);
                openNotificationWithIcon('success', 'Successful', `${message}`, 'bottomLeft');
                const updatedSourceList = [...branchData, data];
                setBranchData(updatedSourceList);
                const updatedTempList = applySearchAndFilters(updatedSourceList, searchInputValue, filterValues);
                setTempBranchData(updatedTempList);
                onClose();
            }catch(error){
                if (error.response) {
                    openNotificationWithIcon('error', 'Branch creation failed', `${error.response.data.message}`, 'bottomLeft');
                } else {
                    openNotificationWithIcon('error', 'Branch creation failed', `${error.message}`, 'bottomLeft');
                }
            }finally{
                setShowPopconfirm(false);
            }            
        }
        if (actionState === "edit") {
            try {
                const { message, data } = await MasterDataAPI.updateBranch(branchId, trimmedPayload, companyId, token);
                openNotificationWithIcon('success', 'Successful', `${message}`, 'bottomLeft');
                const updatedBranch = data;                
                const updatedSourceList = branchData.map(branch =>
                    branch.id === branchId ? updatedBranch : branch
                );
                setBranchData(updatedSourceList);                
                const updatedTempList = applySearchAndFilters(updatedSourceList, searchInputValue, filterValues);
                setTempBranchData(updatedTempList);

                onClose();
            } catch (error) {
                if (error.response) {
                    openNotificationWithIcon('error', 'Branch update failed', `${error.response.data.message}`, 'bottomLeft');
                } else {
                    openNotificationWithIcon('error', 'Branch update failed', `${error.message}`, 'bottomLeft');
                }
            } finally {
                setShowPopconfirm(false);
            }
        }
    };

    const handleCreateUpdateClick = async (e) => {
        e.preventDefault();
        let validation = validateBeforeSubmit();

        if (actionState === "add" && validation) {
            //setShowPopconfirm(true)
            handleSubmit();
        }
        else {
            if (validation) {
                setShowPopconfirm(true)
            } else {
                setShowPopconfirm(false)
            }
        }
    };

    const handleCancel = () => {
        setCancelButtonDisabled(true);
        openNotificationWithIcon('error', 'Abort', `${actionState === "add" ? "Create" : "Update"} cancelled`, 'bottomLeft');
        setTimeout(() => {
            setShowPopconfirm(false);
            setCancelButtonDisabled(false);
        }, 300);
    };

    useEffect(() => {
        setBtnPermission({
            "add": false,
            "edit": false,
            "delete": false,
            "view": false,
            "activeInactive": false
        })
        if (role.length > 0) {
            role.forEach(value => {
                if (value.restricted) {
                    if (value.roleName === "Company Admin") {
                        setBtnPermission({
                            "add": true,
                            "edit": true,
                            "delete": true,
                            "view": true,
                            "activeInactive": true
                        })
                    } else if (value.roleName === "Admin") {
                        setBtnPermission({
                            "add": true,
                            "edit": true,
                            "delete": true,
                            "view": true,
                            "activeInactive": true
                        })
                    }
                } else if (value?.permissions) {
                    console.log('value :', value)

                    const permissionData = value.permissions.find(data => data.permissionName === "Branch");

                    if (permissionData) {
                        permissionData.permissionLevel.map(value => {
                            switch (value) {
                                case "create":
                                    setBtnPermission(prevState => ({
                                        ...prevState,
                                        add: true
                                    }));
                                    break;
                                case "view":
                                    setBtnPermission(prevState => ({
                                        ...prevState,
                                        view: true
                                    }));
                                    break;
                                case "edit":
                                    setBtnPermission(prevState => ({
                                        ...prevState,
                                        edit: true
                                    }));
                                    break;
                                case "delete":
                                    setBtnPermission(prevState => ({
                                        ...prevState,
                                        delete: true
                                    }));
                                    break;
                                case "Active / Inactive":
                                    setBtnPermission(prevState => ({
                                        ...prevState,
                                        activeInactive: true
                                    }));
                                    break;
                            }
                        })
                    } else {
                        setBtnPermission({
                            "add": false,
                            "edit": false,
                            "delete": false,
                            "view": false,
                            "activeInactive": false
                        })
                    }
                }
            });
        }
    }, []);

    const filterTags = filterValues.filter(filterObject => filterObject.value.trim() !== "");

    return (
        <>
            <Flex gap={20} justify='space-between' align='center' style={{
                margin: '16px',
                padding: '16px 24px',
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
            }}>
                <Breadcrumb
                    items={[
                        {
                            // href: '',
                            title: <HomeOutlined />,
                        },
                        {
                            title: ' Master Data / Branch',
                        },
                    ]}
                />
                <Flex justify='center' align='center' gap={12}>
                    <Button
                        icon={
                            <FilterOutlined
                                style={{ paddingLeft: 8, paddingRight: 8 }}
                            />
                        }
                        onClick={showFilterDrawer}
                    />
                    <Search
                        placeholder='Search'
                        value={searchInputValue}
                        onChange={(e) => setSearchInputValue(e.target.value)}
                    />
                    {btnPermission.add && (
                        <Button type="primary" onClick={() => {
                            form.resetFields();                            
                            setInitialBranchData({ code: "", name: "" });
                            setActionState('add');
                            setOpen(true);
                        }} >Add New</Button>
                    )}
                </Flex>

            </Flex>
            <div style={{ margin: '16px' }}>
                {
                    filterTags.length !== 0 &&
                    <Flex align='center' gap={12}>
                        {filterTags.map(item => (
                            <Tag bordered={false} closable color="success" key={item?.key} onClose={() => handleRemoveTag(item?.key)}>
                                {item?.value}
                            </Tag>
                        ))}
                    </Flex>
                }
            </div>
            <div style={{
                margin: '16px',
                padding: 24,
                minHeight: "78svh",
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
            }}>
                {/* {btnPermission.view ? ( */}
                {isBranchListLoading ? (<><CommonSkeletonLoader /></>) : (<>
                    <Table
                        columns={columns}
                        dataSource={tempBranchData}
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            current: currentPage,
                            pageSize,
                            onChange: (page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            },
                            onShowSizeChange: (current, size) => {
                                setCurrentPage(1);
                                setPageSize(size);
                            },
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            selectProps: {
                                showSearch: false,
                                inputReadOnly: true,
                                onInputKeyDown: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                },
                                onKeyDown: (e) => {
                                    // Allow only arrow keys, Enter, Escape, Tab
                                    if (!['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab'].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }
                            }
                        }}
                    />
                </>)}
                {/* ) : (
                    <Flex vertical align='center' justify='center' gap={16} style={{ minHeight: 300, opacity:0.45 }} >
                        <WarningOutlined style={{ fontSize: 48 }} />
                        <Text>Permission needed to view</Text>
                    </Flex>
                )} */}
                <Drawer title={actionState === 'add' ? "Add Branch" : "Update Branch"}
                    onClose={onClose}
                    open={open}
                    footer={[
                        <Flex justify='flex-end' align='center' gap='large' key="footer-buttons">
                            <Button onClick={onClose}>
                                Cancel
                            </Button>
                            {actionState === 'add' ? (
                                <Button type="primary" onClick={handleSubmit}>
                                    Create
                                </Button>
                            ) : (<Popconfirm
                                title={`Do you want to update the branch?`}
                                open={showPopconfirm}
                                onConfirm={() => handleSubmit()}
                                onCancel={handleCancel}
                                okText="Yes"
                                cancelText="No"
                                cancelButtonProps={{ disabled: cancelButtonDisabled }}
                            >
                                <Button 
                                    type="primary" 
                                    onClick={handleCreateUpdateClick}
                                    disabled={!isFormModified()}
                                >
                                    Update
                                </Button>
                            </Popconfirm>)}
                        </Flex>
                    ]}
                >
                    <Form 
                        form={form}
                        layout="vertical"
                        onValuesChange={(changedValues, allValues) => {    
                            if(actionState === 'add')return;

                        }}
                    >
                        <Form.Item 
                            label="Branch Code"
                            name="code"
                            rules={getBranchCodeRules()}
                        >
                            <Input 
                                placeholder="Code"
                                autoComplete="new-code" 
                            />
                        </Form.Item>
                        <Form.Item 
                            label="Branch Name"
                            name="name"
                            rules={getBranchNameRules()}
                        >
                            <Input
                                placeholder="Name"
                                autoComplete="new-name" 
                            />
                        </Form.Item>
                    </Form>
                </Drawer>
                <FilterComponent
                    openDrawer={openFilter}
                    closeDrawer={onCloseFilter}
                    data={branchData}
                    handleFilterDetailsChange={handleFilterDetailsChange}
                    filterValues={filterValues}
                    screenName="masterData_branch"
                    resetFilterValues={() => setFilterValues([])}
                />
                {contextHolder}
            </div>
        </>
    )
}
